import React from "react";
import {
  Card,
  CardContent,
  CardActions,
  Typography,
  useTheme,
  Link,
  Box,
  Avatar
} from "@mui/material";
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PropTypes from 'prop-types';

const PartnerCard = ({ partner, sx = {} }) => {
  const theme = useTheme();

  return (
    <Card
      variant="outlined"
      sx={{
        display: "flex",
        flexDirection: "column",
        minHeight: { xs: "220px", md: "250px" },
        backgroundColor: "white",
        p: 3,
        width: '100%',
        borderRadius: 2,
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'visible',
        '&:hover': {
          boxShadow: theme.shadows[3],
          transform: 'translateY(-2px)'
        },
        ...sx
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2.5,
          mb: 2.5,
          flexDirection: { xs: 'column', sm: 'row' },
          textAlign: { xs: 'center', sm: 'left' }
        }}
      >
        {partner.logo_url ? (
          <Box
            sx={{
              width: { xs: '80px', sm: '80px' },
              height: { xs: '80px', sm: '80px' },
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              overflow: 'hidden',
              borderRadius: 1,
              bgcolor: 'background.paper'
            }}
          >
            <Box
              component="img"
              src={partner.logo_url}
              alt={`${partner.name} logo`}
              sx={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                objectPosition: 'center'
              }}
            />
          </Box>
        ) : (
          <Avatar
            variant="rounded"
            sx={{
              width: { xs: '80px', sm: '80px' },
              height: { xs: '80px', sm: '80px' },
              bgcolor: theme.palette.primary.light,
              color: theme.palette.primary.contrastText,
              fontSize: '1.8rem',
              fontWeight: 'bold'
            }}
          >
            {partner.name?.charAt(0).toUpperCase() || 'P'}
          </Avatar>
        )}
        
        <Box sx={{ 
          flex: 1,
          width: { xs: '100%', sm: 'auto' }
        }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              fontSize: { xs: "16px", md: "18px" },
              lineHeight: 1.3,
              mb: 1,
              color: 'text.primary'
            }}
          >
            {partner.name || 'Nome não disponível'}
          </Typography>
        </Box>
      </Box>

      <CardContent
        sx={{
          flex: 1,
          p: 0,
          "&:last-child": { pb: 0 }
        }}
      >
        <Typography
          variant="body2"
          sx={{
            fontSize: { xs: "13px", md: "14px" },
            color: 'text.secondary',
            lineHeight: 1.6,
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            mb: 2,
            textAlign: { xs: 'center', sm: 'left' }
          }}
        >
          {partner.description}
        </Typography>
      </CardContent>

      {partner.url && (
        <CardActions 
          sx={{ 
            mt: "auto",
            p: 0,
            display: 'flex',
            justifyContent: { xs: 'center', sm: 'flex-start' }
          }}
        >
          <Link
            href={partner.url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'primary.main',
              textDecoration: 'none',
              fontSize: { xs: "13px", md: "14px" },
              fontWeight: 500,
              '&:hover': {
                textDecoration: 'underline'
              }
            }}
          >
            <Typography variant="button" sx={{ mr: 0.5, textTransform: 'none' }}>
              Visitar site
            </Typography>
            <OpenInNewIcon sx={{ fontSize: 16 }} />
          </Link>
        </CardActions>
      )}
    </Card>
  );
};

PartnerCard.propTypes = {
  partner: PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    description: PropTypes.string,
    url: PropTypes.string,
    logo_url: PropTypes.string
  }).isRequired,
  sx: PropTypes.object
};

export default PartnerCard; 